/**
 * Browser Information Utility
 * Collects browser, device, and system information
 */

/**
 * Get comprehensive browser and device information
 * @returns {Object} Browser information
 */
export function getBrowserInfo() {
  const ua = navigator.userAgent;
  
  return {
    // Browser details
    browser: detectBrowser(ua),
    browserVersion: detectBrowserVersion(ua),
    
    // Operating system
    os: detectOS(ua),
    osVersion: detectOSVersion(ua),
    
    // Device information
    device: detectDevice(ua),
    isMobile: isMobileDevice(),
    isTablet: isTabletDevice(),
    
    // Screen information
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    
    // Additional details
    language: navigator.language,
    languages: navigator.languages,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookiesEnabled: navigator.cookieEnabled,
    onlineStatus: navigator.onLine,
    
    // User agent (full string)
    userAgent: ua
  };
}

/**
 * Detect browser name
 * @param {string} ua - User agent string
 * @returns {string} Browser name
 */
function detectBrowser(ua) {
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('MSIE') || ua.includes('Trident')) return 'Internet Explorer';
  return 'Unknown';
}

/**
 * Detect browser version
 * @param {string} ua - User agent string
 * @returns {string} Browser version
 */
function detectBrowserVersion(ua) {
  let match;
  
  if (ua.includes('Firefox')) {
    match = ua.match(/Firefox\/(\d+\.\d+)/);
  } else if (ua.includes('Edg')) {
    match = ua.match(/Edg\/(\d+\.\d+)/);
  } else if (ua.includes('Chrome')) {
    match = ua.match(/Chrome\/(\d+\.\d+)/);
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    match = ua.match(/Version\/(\d+\.\d+)/);
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    match = ua.match(/(?:Opera|OPR)\/(\d+\.\d+)/);
  }
  
  return match ? match[1] : 'Unknown';
}

/**
 * Detect operating system
 * @param {string} ua - User agent string
 * @returns {string} OS name
 */
function detectOS(ua) {
  if (ua.includes('Windows NT 10.0')) return 'Windows 10/11';
  if (ua.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (ua.includes('Windows NT 6.2')) return 'Windows 8';
  if (ua.includes('Windows NT 6.1')) return 'Windows 7';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS X')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
}

/**
 * Detect OS version
 * @param {string} ua - User agent string
 * @returns {string} OS version
 */
function detectOSVersion(ua) {
  let match;
  
  if (ua.includes('Mac OS X')) {
    match = ua.match(/Mac OS X (\d+[._]\d+[._]\d+)/);
    return match ? match[1].replace(/_/g, '.') : 'Unknown';
  }
  
  if (ua.includes('Android')) {
    match = ua.match(/Android (\d+\.\d+)/);
    return match ? match[1] : 'Unknown';
  }
  
  if (ua.includes('Windows NT')) {
    match = ua.match(/Windows NT (\d+\.\d+)/);
    return match ? match[1] : 'Unknown';
  }
  
  return 'Unknown';
}

/**
 * Detect device type
 * @param {string} ua - User agent string
 * @returns {string} Device type
 */
function detectDevice(ua) {
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'Tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'Mobile';
  }
  return 'Desktop';
}

/**
 * Check if device is mobile
 * @returns {boolean} True if mobile
 */
function isMobileDevice() {
  return /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(navigator.userAgent);
}

/**
 * Check if device is tablet
 * @returns {boolean} True if tablet
 */
function isTabletDevice() {
  return /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(navigator.userAgent);
}

/**
 * Get connection information
 * @returns {Object} Connection info
 */
export function getConnectionInfo() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  
  if (!connection) {
    return {
      type: 'unknown',
      effectiveType: 'unknown',
      downlink: null,
      rtt: null
    };
  }
  
  return {
    type: connection.type || 'unknown',
    effectiveType: connection.effectiveType || 'unknown',
    downlink: connection.downlink || null,
    rtt: connection.rtt || null,
    saveData: connection.saveData || false
  };
}

/**
 * Get performance metrics
 * @returns {Object} Performance info
 */
export function getPerformanceInfo() {
  if (!window.performance || !window.performance.timing) {
    return null;
  }
  
  const timing = window.performance.timing;
  const navigation = window.performance.navigation;
  
  return {
    pageLoadTime: timing.loadEventEnd - timing.navigationStart,
    domReadyTime: timing.domContentLoadedEventEnd - timing.navigationStart,
    connectTime: timing.connectEnd - timing.connectStart,
    requestTime: timing.responseEnd - timing.requestStart,
    navigationType: navigation.type // 0=navigate, 1=reload, 2=back/forward
  };
}
